import { useMemo, useState } from "react";
import ServiceFormModal from "@/pages/admin/services/components/ServiceFormModal";
import { useServicesData } from "@/hooks/useServices";
import { supabase } from "@/lib/supabase";
import { logAudit } from "@/lib/audit";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/context/AuthContext";
import type { WorkshopService } from "@/types";

const currency = new Intl.NumberFormat("es-DO", {
  style: "currency",
  currency: "DOP",
  minimumFractionDigits: 2,
});

const CATEGORY_ICONS: Record<string, string> = {
  Montaje: "ri-tools-line",
  Balanceo: "ri-refresh-line",
  Alineación: "ri-roadster-line",
  Reparación: "ri-hammer-line",
  "Cambio de aceite": "ri-drop-line",
  "Mecánica general": "ri-settings-5-line",
  Otro: "ri-apps-2-line",
};

export default function AdminServicesPage() {
  const toast = useToast();
  const { profile } = useAuth();
  const { services, loading, error, reload } = useServicesData();

  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<WorkshopService | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return services.filter((service) => {
      if (categoryFilter && service.category !== categoryFilter) return false;
      if (statusFilter === "active" && !service.active) return false;
      if (statusFilter === "inactive" && service.active) return false;
      if (!term) return true;
      return `${service.name} ${service.description ?? ""}`.toLowerCase().includes(term);
    });
  }, [services, search, categoryFilter, statusFilter]);

  const stats = useMemo(() => {
    const active = services.filter((s) => s.active).length;
    const categories = new Set(services.map((s) => s.category).filter(Boolean)).size;
    const avg =
      services.length > 0
        ? services.reduce((sum, s) => sum + Number(s.price ?? 0), 0) / services.length
        : 0;
    return { total: services.length, active, categories, avg };
  }, [services]);

  const categories = useMemo(
    () => Array.from(new Set(services.map((s) => s.category).filter(Boolean))) as string[],
    [services],
  );

  const openCreate = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const openEdit = (service: WorkshopService) => {
    setEditing(service);
    setFormOpen(true);
  };

  const handleToggleActive = async (service: WorkshopService) => {
    setTogglingId(service.id);
    const { error: updateError } = await supabase
      .from("services")
      .update({ active: !service.active, updated_at: new Date().toISOString() })
      .eq("id", service.id);
    setTogglingId(null);
    if (updateError) {
      toast.error("No se pudo actualizar el servicio.");
      return;
    }
    await logAudit({
      userId: profile?.id ?? null,
      userName: `${profile?.full_name ?? ""} ${profile?.last_name ?? ""}`.trim() || "Usuario",
      action: "editar",
      entity: "services",
      entityId: service.id,
      branchId: profile?.branch_id ?? null,
      oldValue: { active: service.active },
      newValue: { active: !service.active },
    });
    toast.success(service.active ? "Servicio desactivado." : "Servicio activado.");
    reload();
  };

  const statCards = [
    { key: "total", label: "Servicios", value: String(stats.total), icon: "ri-tools-line", tone: "primary" },
    { key: "active", label: "Activos", value: String(stats.active), icon: "ri-checkbox-circle-line", tone: "accent" },
    { key: "categories", label: "Categorías", value: String(stats.categories), icon: "ri-apps-2-line", tone: "secondary" },
    { key: "avg", label: "Precio promedio", value: currency.format(stats.avg), icon: "ri-money-dollar-circle-line", tone: "secondary" },
  ];

  const toneClass: Record<string, string> = {
    primary: "bg-primary-100 text-primary-700",
    accent: "bg-accent-100 text-accent-800",
    secondary: "bg-secondary-200 text-secondary-900",
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-foreground-950">Servicios del Taller</h1>
          <p className="mt-1.5 text-sm text-foreground-500">
            Montaje, balanceo, alineación y reparación como servicios vendibles junto a los productos.
          </p>
        </div>
        <button
          type="button"
          onClick={openCreate}
          className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
        >
          <i className="ri-add-line text-base"></i>
          Nuevo servicio
        </button>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {statCards.map((card) => (
          <div
            key={card.key}
            className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-100 px-4 py-3"
          >
            <span className={`flex h-9 w-9 items-center justify-center rounded-md ${toneClass[card.tone]}`}>
              <i className={`${card.icon} text-base`}></i>
            </span>
            <div className="min-w-0">
              <p className="font-heading truncate text-lg font-bold leading-none text-foreground-950">
                {card.value}
              </p>
              <p className="mt-1 text-[11px] text-foreground-500">{card.label}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="flex flex-col gap-3 rounded-lg border border-background-200 bg-background-100 p-4 lg:flex-row lg:items-center">
        <div className="relative flex-1">
          <span className="pointer-events-none absolute left-3 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center text-foreground-400">
            <i className="ri-search-line text-base"></i>
          </span>
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Buscar servicio..."
            className="w-full rounded-md border border-background-300 bg-background-50 py-2.5 pl-10 pr-3 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-300 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
          />
        </div>
        <div className="flex flex-wrap gap-3">
          <select
            value={categoryFilter}
            onChange={(event) => setCategoryFilter(event.target.value)}
            className="rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-950 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
          >
            <option value="">Todas las categorías</option>
            {categories.map((category) => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
            className="rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-950 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
          >
            <option value="all">Todos</option>
            <option value="active">Activos</option>
            <option value="inactive">Inactivos</option>
          </select>
        </div>
      </div>

      {error ? (
        <div className="flex flex-col items-start gap-3 rounded-lg border border-primary-200 bg-primary-50 p-4 text-sm text-primary-800 sm:flex-row sm:items-center sm:justify-between">
          <span className="flex items-center gap-2">
            <i className="ri-error-warning-line text-base"></i>
            {error}
          </span>
          <button
            type="button"
            onClick={reload}
            className="whitespace-nowrap rounded-md border border-primary-300 bg-background-50 px-3 py-1.5 text-xs font-semibold text-primary-700"
          >
            Reintentar
          </button>
        </div>
      ) : null}

      {loading ? (
        <div className="flex items-center justify-center gap-3 rounded-lg border border-background-200 bg-background-100 py-14 text-sm text-foreground-500">
          <i className="ri-loader-4-line animate-spin text-lg"></i>
          Cargando servicios...
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-background-300 bg-background-100 px-4 py-14 text-center">
          <span className="flex h-11 w-11 items-center justify-center rounded-full bg-background-200 text-foreground-500">
            <i className="ri-tools-line text-2xl"></i>
          </span>
          <p className="text-sm font-semibold text-foreground-800">Sin servicios</p>
          <p className="max-w-sm text-xs text-foreground-500">
            Crea los servicios del taller (montaje, balanceo, alineación, reparación) para venderlos en
            el POS.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {filtered.map((service) => (
            <div
              key={service.id}
              className="flex flex-col gap-4 rounded-lg border border-background-200 bg-background-100 p-4"
            >
              <div className="flex items-start gap-3">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                  <i className={`${CATEGORY_ICONS[service.category ?? "Otro"] ?? "ri-tools-line"} text-xl`}></i>
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-foreground-950">{service.name}</p>
                  <div className="mt-1 flex flex-wrap items-center gap-1.5">
                    <span className="rounded-full bg-secondary-100 px-2 py-0.5 text-[10px] font-semibold text-secondary-900">
                      {service.category || "General"}
                    </span>
                    <span className="rounded-full bg-background-200 px-2 py-0.5 text-[10px] font-semibold text-foreground-700">
                      {service.duration_minutes ?? 0} min
                    </span>
                    {!service.active ? (
                      <span className="rounded-full bg-primary-100 px-2 py-0.5 text-[10px] font-semibold text-primary-700">
                        Inactivo
                      </span>
                    ) : null}
                  </div>
                </div>
              </div>

              {service.description ? (
                <p className="line-clamp-2 text-xs leading-relaxed text-foreground-500">
                  {service.description}
                </p>
              ) : null}

              <div className="mt-auto flex items-center justify-between gap-2 border-t border-background-200 pt-3">
                <p className="font-heading text-lg font-bold text-foreground-950">
                  {currency.format(Number(service.price ?? 0))}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => openEdit(service)}
                    className="flex h-9 w-9 items-center justify-center rounded-md border border-background-300 bg-background-50 text-foreground-600 transition-colors hover:bg-background-100"
                  >
                    <i className="ri-edit-2-line text-base"></i>
                  </button>
                  <button
                    type="button"
                    disabled={togglingId === service.id}
                    onClick={() => handleToggleActive(service)}
                    className={`flex h-9 w-9 items-center justify-center rounded-md border transition-colors disabled:opacity-60 ${
                      service.active
                        ? "border-primary-200 bg-primary-50 text-primary-700"
                        : "border-accent-300 bg-accent-50 text-accent-900"
                    }`}
                  >
                    <i
                      className={`${service.active ? "ri-close-circle-line" : "ri-checkbox-circle-line"} text-base`}
                    ></i>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <ServiceFormModal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSaved={reload}
        service={editing}
      />
    </div>
  );
}